import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import RoleAssignmentForm, { RoleAssignment } from './RoleAssignmentForm';
import { Role } from '../../hooks/useFetchRoles';
import { API } from '@/utils/API';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Define the form schema
const roleAssignmentSchema = z.object({
  role_key: z.string().min(1, 'Role is required'),
  resource_type: z.string().optional(),
  resource_id: z.string().optional(),
  resource_label: z.string().optional(),
});

const roleAssignmentFormSchema = z.object({
  user_id: z.string().min(1, 'User ID is required'),
  roleAssignments: z
    .array(roleAssignmentSchema)
    .min(1, 'At least one role assignment is required'),
});

type RoleAssignmentFormData = z.infer<typeof roleAssignmentFormSchema>;

const NewRoleAssignmentForm: React.FC<{
  roles: Record<string, Role>;
  user_id: string;
}> = ({ roles, user_id }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(
    !user_id ? 'User ID is required' : ''
  );
  const [submissionSuccess, setSubmissionSuccess] = useState('');
  const [showAddAssignmentForm, setShowAddAssignmentForm] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  const form = useForm<RoleAssignmentFormData>({
    resolver: zodResolver(roleAssignmentFormSchema),
    defaultValues: {
      user_id: user_id,
      roleAssignments: [],
    },
    mode: 'onChange',
  });

  const { append, remove } = useFieldArray({
    control: form.control,
    name: 'roleAssignments',
  });

  useEffect(() => {
    const fetchUserEmail = async () => {
      if (!user_id) return;

      setIsLoadingEmail(true);
      try {
        const response = await API.authenticated.admin.fetch(
          `/users/${user_id}`
        );
        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.user?.email_address || '');
        } else {
          const errorData = await response.json().catch(() => ({}));
          setSubmissionError(errorData.message || 'Failed to fetch user email');
        }
      } catch (error) {
        console.error('Error fetching user email:', error);
        setSubmissionError('Failed to fetch user email');
      } finally {
        setIsLoadingEmail(false);
      }
    };

    fetchUserEmail();
  }, [user_id]);

  const onSubmit = async (data: RoleAssignmentFormData) => {
    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      // Create role assignments one by one
      const results = await Promise.allSettled(
        data.roleAssignments.map(assignment =>
          API.authenticated.admin.fetch('/role_assignments', {
            method: 'POST',
            body: JSON.stringify({
              role_assignment: {
                user_id: user_id,
                role_key: assignment.role_key,
                resource_type: assignment.resource_type,
                resource_id: assignment.resource_id,
              },
            }),
          })
        )
      );

      const errors: string[] = [];
      let successCount = 0;

      // Process results and await JSON parsing
      for (let index = 0; index < results.length; index++) {
        const result = results[index];
        if (result.status === 'fulfilled') {
          const response = result.value;
          if (response.ok) {
            successCount++;
          } else {
            try {
              const responseData: { message?: string } = await response.json();
              errors.push(
                responseData.message ||
                  `Failed to create role assignment ${index + 1}`
              );
            } catch {
              errors.push(`Failed to create role assignment ${index + 1}`);
            }
          }
        } else {
          errors.push(
            `Failed to create role assignment ${index + 1}: ${result.reason}`
          );
        }
      }

      if (errors.length > 0) {
        throw new Error(errors.join('; '));
      }

      if (successCount > 0) {
        setSubmissionSuccess(
          `Successfully created ${successCount} role assignment${successCount > 1 ? 's' : ''}`
        );
        form.reset();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleAssignments = form.watch('roleAssignments');

  const onAddAssignment = (assignment: RoleAssignment) => {
    const isDuplicate = roleAssignments.some(
      existing =>
        existing.role_key === assignment.role_key &&
        existing.resource_type === assignment.resource_type &&
        existing.resource_id === assignment.resource_id
    );

    if (!isDuplicate) {
      append(assignment);
    }
    setShowAddAssignmentForm(false);
  };

  if (showAddAssignmentForm) {
    return (
      <RoleAssignmentForm
        onSubmit={onAddAssignment}
        onCancel={() => setShowAddAssignmentForm(false)}
        roles={roles}
      />
    );
  }

  return (
    <Form {...form}>
      <Label htmlFor="email" className="mb-2 text-sm font-medium text-gray-700">
        Email
      </Label>
      <Input
        id="email"
        type="email"
        placeholder={isLoadingEmail ? 'Loading...' : 'Enter email address'}
        value={userEmail}
        className="w-full"
        disabled={true}
      />
      {/* Role Assignments Table */}
      <div className="mt-4">
        <div className="flex justify-end mb-4">
          <Button onClick={() => setShowAddAssignmentForm(true)}>
            Add Role Assignment
          </Button>
        </div>
        <div className="rounded-lg border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Name
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Role Assignment
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Selection(s)
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roleAssignments.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No role assignments added yet
                  </td>
                </tr>
              ) : (
                roleAssignments.map((result, index) => (
                  <tr
                    key={`${result.role_key}-${result.resource_type}-${result.resource_id}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {roles[result.role_key]?.name}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.resource_type}
                    </td>

                    <td className="text-wrap px-6 py-4 text-sm text-gray-500">
                      {result.resource_label}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">
                      <button
                        className="hover:text-red-400 cursor-pointer"
                        aria-label={`Remove role assignment for ${roles[result.role_key]?.name}`}
                        onClick={() => remove(index)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10 flex gap-4">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => (window.location.href = `/admin/users/${user_id}`)}
        >
          Back
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={form.handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Role Assignments'}
        </Button>
      </div>

      {form.formState.errors.roleAssignments && (
        <p className="text-red-500 text-sm text-center mt-10">
          {form.formState.errors.roleAssignments.message}
        </p>
      )}

      {submissionSuccess && (
        <p className="text-green-500 text-center">{submissionSuccess}</p>
      )}
      {submissionError && (
        <p className="text-red-500 text-center">{submissionError}</p>
      )}
    </Form>
  );
};

export default NewRoleAssignmentForm;
